// "Your day, at a glance" and "Make time": the shapes the server and the web share, and the pure
// rules both use to order the day and say what is happening now and next. No runtime
// dependencies, so the web bundle stays small; callers pass the clock.
import type { WorkView } from "./api";

/** What a focus block is for: a card or a task. Its title becomes the block's title. */
export interface FocusTarget {
  kind: "card" | "task";
  id: string;
}

/** The lengths "Make time" offers, in minutes. */
export const FOCUS_DURATIONS = [15, 30, 60, 90] as const;
export type FocusDuration = (typeof FOCUS_DURATIONS)[number];
export const DEFAULT_FOCUS_DURATION: FocusDuration = 30;

export function isFocusDuration(n: number): n is FocusDuration {
  return (FOCUS_DURATIONS as readonly number[]).includes(n);
}

export type AgendaItemKind = "event" | "focus" | "task";

// ---------------------------------------------------------------------------------------------
// Calendars in "Your day": which ones show, what they are called, and their colour

/** Calm colours only (no red); the web maps slot n to `var(--cal-n)`. */
export const CALENDAR_COLOR_SLOTS = 6;

/** One calendar's identity across accounts (every account has a calendar called `primary`). */
export function calendarKey(accountId: string, calendarId: string): string {
  return `${accountId}/${calendarId}`;
}

/** A stable palette slot for `key` (FNV-1a), before collisions with other calendars are moved. */
export function calendarColorSlot(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h % CALENDAR_COLOR_SLOTS;
}

/**
 * Colours for calendars in display order. A colour already given (`color`, kept in settings once
 * the owner changes anything about that calendar) never moves; the rest take their own slot, or
 * the next free one when an earlier calendar has it, so two calendars rarely share a colour.
 */
export function assignCalendarColors<T extends { key: string; color?: number | null }>(
  list: readonly T[],
): (T & { color: number })[] {
  const used = new Set<number>();
  for (const c of list) if (typeof c.color === "number") used.add(c.color);
  return list.map((c) => {
    if (typeof c.color === "number") return { ...c, color: c.color };
    let slot = calendarColorSlot(c.key);
    for (let i = 0; i < CALENDAR_COLOR_SLOTS && used.has(slot); i++)
      slot = (slot + 1) % CALENDAR_COLOR_SLOTS;
    used.add(slot);
    return { ...c, color: slot };
  });
}

/** Where an event on the rail comes from (focus blocks and tasks have none). */
export interface CalendarSource {
  key: string;
  /** "School", "Personal", or the account's address until the owner renames it. */
  label: string;
  color: number;
}

/** A calendar in the "Your day" settings (/settings/calendar). */
export interface DayCalendarView extends CalendarSource {
  accountId: string;
  accountEmail: string;
  calendarId: string;
  /** The owner's own name for it; null while it uses the default. */
  customLabel: string | null;
  /** Shows in "Your day" (default: each account's primary calendar). */
  show: boolean;
  isPrimary: boolean;
  /** Separate setting: whether it counts as busy when MeeMail suggests times. */
  countsForAvailability: boolean;
  /** Added only for "Your day", so it can be removed here. */
  dayOnly: boolean;
}

export interface DayCalendarPatch {
  accountId: string;
  calendarId: string;
  show?: boolean;
  /** Null (or empty) goes back to the default name. */
  label?: string | null;
}

export interface DayCalendarAdd {
  accountId: string;
  calendarId: string;
  label?: string;
}

export const MAX_CALENDAR_LABEL = 40;

export interface AgendaItem {
  /** Stable key for the list. */
  id: string;
  kind: AgendaItemKind;
  title: string;
  /** UTC instants. `start` null: an all-day event, or a task due any time today. */
  start: string | null;
  /** Null for all-day events and for tasks (a task is a moment, not a stretch). */
  end: string | null;
  accountId: string | null;
  /** The event in Google Calendar (opens in a new tab); null for tasks. */
  link: string | null;
  /** A focus block MeeMail made: its ledger row (Remove uses it) and what it was for. */
  focus: {
    holdId: string | null;
    approvalId: string | null;
    target: FocusTarget | null;
    /** Approved and on its way, not yet confirmed in Google Calendar. */
    pending: boolean;
  } | null;
  task: { taskId: string; cardId: string | null; threadKey: string | null } | null;
  /** The calendar an event is on; null for focus blocks and tasks (they keep a neutral look). */
  source: CalendarSource | null;
}

export interface AgendaView {
  /** The owner's zone; every time on the rail is read in it. */
  timeZone: string;
  /** Today in the owner's zone, `YYYY-MM-DD`. */
  date: string;
  dayStart: string;
  dayEnd: string;
  calendar: {
    connected: boolean;
    /** When nothing is connected: the account a "Connect" link should start with. */
    connectAccountId: string | null;
  };
  /** The calendars shown today, in settings order (the rail's filter chips). */
  calendars: CalendarSource[];
  /** Ordered with `orderAgenda`. */
  items: AgendaItem[];
  /** Calm, factual notes (a calendar that could not be read just now). */
  notes: string[];
}

export interface FocusSlot {
  start: string;
  end: string;
  /** The owner's zone (the event is written with it). */
  tz: string;
}

export interface FocusSlotsRequest {
  target: FocusTarget;
  durationMin: number;
}

export interface FocusSlotsResponse {
  connected: boolean;
  /** The calendar the block would go on (the target's account when it has one connected). */
  accountId: string | null;
  email: string | null;
  timeZone: string;
  /** The block's title: the card's or the task's title as it is now. */
  title: string;
  durationMin: number;
  /** Earliest first: free, within working hours, today or tomorrow. */
  slots: FocusSlot[];
  notes: string[];
}

export interface FocusRequest {
  target: FocusTarget;
  slot: { start: string; end: string };
  /** The title the owner saw; the server refuses if the card or task has been renamed since. */
  title: string;
  /**
   * A card only (U3 "Big cards get time"): once approved, the card leaves the round and waits
   * under Lined up until the block starts.
   */
  lineUp?: boolean;
}

export interface FocusResult {
  kind: "approval";
  approvalId: string;
  work: WorkView;
  deduped: boolean;
  /** True when the card was lined up behind the block (`lineUp`). */
  linedUp?: boolean;
}

/** POST /api/calendar/focus/suggest: one time tomorrow morning for a big card (U3). */
export interface FocusSuggestResponse {
  connected: boolean;
  accountId: string | null;
  email: string | null;
  timeZone: string;
  title: string;
  durationMin: number;
  /** Tomorrow at 10:00 when free, else the nearest free morning time; null when none. */
  slot: FocusSlot | null;
  notes: string[];
}

/** Remove one focus block: by the approval that made it (Undo) or by its ledger row (the rail). */
export interface FocusRemoveRequest {
  approvalId?: string;
  holdId?: string;
}

export interface FocusRemoveResult {
  kind: "approval";
  approvalId: string;
  work: WorkView;
  deduped: boolean;
}

// ---------------------------------------------------------------------------------------------
// Ordering and "now / next"

const KIND_ORDER: Record<AgendaItemKind, number> = { event: 0, focus: 1, task: 2 };

/** 0: all-day events, 1: timed items, 2: tasks due any time today. */
function band(item: AgendaItem): number {
  if (item.start) return 1;
  return item.kind === "task" ? 2 : 0;
}

const ms = (iso: string | null) => (iso ? Date.parse(iso) : Number.NaN);

/**
 * The day in reading order: all-day events, then timed items by start (then events before focus
 * blocks before tasks, then the earlier end), then tasks for any time today. Items with the same id appear
 * once (the first one wins).
 */
export function orderAgenda(items: readonly AgendaItem[]): AgendaItem[] {
  const seen = new Set<string>();
  const unique = items.filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
  const timed = (a: AgendaItem, b: AgendaItem) =>
    band(a) === 1 ? ms(a.start) - ms(b.start) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind] : 0;
  const ends = (a: AgendaItem, b: AgendaItem) => (a.end && b.end ? ms(a.end) - ms(b.end) : 0);
  return unique.sort(
    (a, b) =>
      band(a) - band(b) ||
      timed(a, b) ||
      ends(a, b) ||
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      a.title.localeCompare(b.title),
  );
}

export interface DayMoment {
  /** Timed events and focus blocks under way at `now`. */
  now: AgendaItem[];
  /** The first timed item that starts after `now` (a task with a time counts). */
  next: AgendaItem | null;
  /** Nothing is under way: free until `until` (null = the rest of today). */
  free: { until: string | null } | null;
  /** Timed items that are over. */
  earlier: AgendaItem[];
}

/** What is happening now and next on an ordered day. */
export function dayMoment(items: readonly AgendaItem[], nowMs: number): DayMoment {
  const timed = items.filter((i) => i.start);
  const now = timed.filter((i) => i.end !== null && ms(i.start) <= nowMs && nowMs < ms(i.end));
  const next = timed.find((i) => ms(i.start) > nowMs) ?? null;
  const earlier = timed.filter((i) => (i.end !== null ? ms(i.end) : ms(i.start)) <= nowMs);
  return {
    now,
    next,
    free: now.length ? null : { until: next?.start ?? null },
    earlier,
  };
}

/** "in 25 min", "in 1 h", "in 2 h 5 min"; "now" under a minute. */
export function inWords(fromMs: number, toMs: number): string {
  const minutes = Math.round((toMs - fromMs) / 60_000);
  if (minutes < 1) return "now";
  return `in ${durationWords(minutes)}`;
}

/** "25 min", "1 h", "2 h 5 min". */
export function durationWords(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}
